// The colours of a mask model on its product card (owner, 2026-10-20): up to 4 small round photos, this colour first
// with a ring, then the best sellers, and "+25" for the rest. The home rows show one colour per model, so a shopper
// could take a mask for single-colour; the product page already lists them all (assets/sa-color-siblings.js).
// - Every colour is its own product; masks whose English title has the same name before " - " are one model, the
//   same rule as the product page, so the card and the page count the same colours.
// - snippets/card-product.liquid prints an empty row only on mask cards; its height is kept in CSS so the price never
//   moves when the photos arrive. A mask with one colour keeps the row empty.
// - One Storefront API read of every product (English titles, best sellers first; accessories group on their own
//   names and never reach a mask card), kept in this tab for 30 minutes.
// - The photos are not buttons: a tap anywhere on the card opens that mask, where every colour can be picked.
(() => {
  if (window.saCardColours) return;
  const TOKEN = '33a4ab3e489993e75e1cfdb0e4d574bd';
  const API = '/api/2025-07/graphql.json';
  const KEY = 'sa-card-colours-1';
  const TTL = 30 * 60 * 1000;
  const SHOWN = 4;
  const ROWS = '[data-sa-card-colours]:not([data-sa-done])';

  const modelOf = title => String(title || '').replace(/ [—–] /g, ' - ').split(' - ')[0].trim();
  const escape = text => String(text).replace(/[&<>"']/g, character => `&#${character.charCodeAt(0)};`);
  const thumb = url => url ? `${url}${url.includes('?') ? '&' : '?'}width=64&height=64&crop=center` : '';

  // {models: {model: [[handle, image], ...] best sellers first}, byHandle: {handle: model}}
  function group(nodes) {
    const value = {models: {}, byHandle: {}};
    nodes.forEach(node => {
      const model = modelOf(node.title);
      if (!model) return;
      (value.models[model] ||= []).push([node.handle, node.featuredImage?.url || '']);
      value.byHandle[node.handle] = model;
    });
    return value;
  }

  async function request(after) {
    const query = 'query($after:String)@inContext(language:EN){products(first:250,after:$after,sortKey:BEST_SELLING){pageInfo{hasNextPage endCursor}nodes{handle title featuredImage{url}}}}';
    const response = await fetch(API, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': TOKEN},
      body: JSON.stringify({query, variables: {after}})
    });
    if (!response.ok) throw new Error(`storefront ${response.status}`);
    const json = await response.json();
    if (!json.data?.products) throw new Error('storefront data');
    return json.data.products;
  }

  let loading = null;
  let failedAt = 0;
  function load() {
    if (loading) return loading;
    // After a failed read, wait a minute: the card gallery changes the page often, and each change would ask again.
    if (Date.now() - failedAt < 60000) return Promise.reject(new Error('storefront wait'));
    try {
      const saved = JSON.parse(sessionStorage.getItem(KEY));
      if (saved && Date.now() - saved.at < TTL && saved.value?.models) return (loading = Promise.resolve(saved.value));
    } catch (error) {}
    loading = (async () => {
      const nodes = [];
      let after = null;
      for (let page = 0; page < 4; page++) {
        const products = await request(after);
        nodes.push(...products.nodes);
        if (!products.pageInfo.hasNextPage) break;
        after = products.pageInfo.endCursor;
      }
      const value = group(nodes);
      try { sessionStorage.setItem(KEY, JSON.stringify({at: Date.now(), value})); } catch (error) {}
      return value;
    })();
    loading.catch(() => { loading = null; failedAt = Date.now(); });
    return loading;
  }

  function render(row, data) {
    row.dataset.saDone = '';
    const handle = row.dataset.handle;
    const all = data.models[data.byHandle[handle]];
    if (!all || all.length < 2) return;
    const own = all.find(item => item[0] === handle);
    // The ring marks this card's own colour; it is only drawn when that colour is in the list.
    const shown = [own, ...all.filter(item => item !== own)].filter(Boolean).slice(0, SHOWN);
    const more = all.length - shown.length;
    const label = (row.dataset.label || '__N__ Colors').replace('__N__', all.length);
    row.innerHTML = `<span class="visually-hidden">${escape(label)}</span>`
      + shown.map((item, index) => `<span class="sa-cc-dot${index === 0 && own ? ' is-current' : ''}" aria-hidden="true"><img src="${escape(thumb(item[1]))}" alt="" width="32" height="32" loading="lazy" decoding="async"></span>`).join('')
      + (more > 0 ? `<span class="sa-cc-more" aria-hidden="true">+${more}</span>` : '');
  }

  function run() {
    const rows = document.querySelectorAll(ROWS);
    if (!rows.length) return;
    load().then(data => document.querySelectorAll(ROWS).forEach(row => render(row, data))).catch(() => {});
  }

  window.saCardColours = {group, render, run};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run); else run();
  // Cards that arrive later (collection filters and sorting, related products, recently viewed) get theirs too.
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; run(); });
  }).observe(document.documentElement, {childList: true, subtree: true});
})();
